import React, {useEffect, useRef, useState} from "react";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {confirmDialog, ConfirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {TieredMenu} from "primereact/tieredmenu";
import {useUpdateEffect} from "primereact/hooks";
import {DndSortable} from "../../../system/components/dnd-sortable";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import {usePermission} from "../../../system/usePermission";
import {useTranslate} from "../../../system/useTranslate";
import {dateToPersian, isPast} from "../../../system/utils";
import TaskFocusControls from "../../../system/focus/task-focus-controls";
import {RecurrenceBadge} from "../../tasks/components/task-recurrence-badge";
import TaskListFilter, {createTaskListFilters} from "../../tasks/components/task-list-filter";
import {PRIORITY_OPTIONS} from "../../tasks/components/task-filter-model";
import TaskSidebar from "../components/task-sidebar";
import {
    FREE_BOARD_STATUS_OPTIONS,
    freeBoardColumnForTask,
    normalizeFreeBoardColumns,
    serializeFreeBoardColumns,
} from "./free-board-model";
import FreeBoardQuickTaskComposer from "./free-board-quick-task-composer";
import {richTextToPlain} from "../../../system/components/rich-text";
import TaskDecisionTag from "../meetings/task-decision-tag";

const taskKey = (task) => String(task?.documentId || task?.id || "");
const entityKey = (entity) => String(entity?.documentId || entity?.id || entity?.member?.documentId || entity?.member?.id || "");
const relationMember = (value) => value?.member || value;
const entityLabel = (entity) => entity?.fullname || entity?.title || entity?.name || entity?.label || entityKey(entity);

function formatDate(value) {
    if (!value) return "";
    const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? "" : dateToPersian(date, "YYYY/MM/DD");
}

function normalizeTaskChecklist(value) {
    return (Array.isArray(value) ? value : [])
        .filter((item) => item?.id && String(item?.title || "").trim())
        .map((item) => ({...item, done: item.done === true}));
}

function TaskCardChecklist({task, disabled, updateTask}) {
    const translate = useTranslate();
    const [items, setItems] = useState(() => normalizeTaskChecklist(task?.checklist));
    const taskId = taskKey(task);

    useEffect(() => {
        setItems(normalizeTaskChecklist(task?.checklist));
    }, [task?.checklist, taskId]);

    if (!items.length) return null;
    const toggleItem = async (itemId) => {
        if (disabled) return;
        const previous = items;
        const next = items.map((item) => String(item.id) === String(itemId) ? {...item, done: !item.done} : item);
        setItems(next);
        try {
            await updateTask(task, {checklist: next});
        } catch (_) {
            setItems(previous);
        }
    };

    return (
        <div className="flex flex-column gap-2 mt-3 p-2 surface-50 border-1 border-100 border-round-lg" aria-label={translate("task-checklist")} onClick={(event) => event.stopPropagation()}>
            {items.map((item) => {
                const inputId = `free-board-checklist-${taskId}-${item.id}`;
                return (
                    <div key={item.id} className="flex align-items-center gap-2 min-w-0">
                        <Checkbox inputId={inputId} checked={item.done} disabled={disabled} onChange={() => toggleItem(item.id)}/>
                        <label htmlFor={inputId} className={`flex-1 min-w-0 text-3xs line-height-3 cursor-pointer ${item.done ? "line-through text-400" : "text-700"}`}>{item.title}</label>
                    </div>
                );
            })}
        </div>
    );
}

function FreeBoard({
    tasks,
    updateTask,
    createTask,
    updateProject,
    projectData,
    projectAccess,
    boardColumns,
    sidebarBoardColumns,
    resetFiltersKey,
    columnStorageField = "freeBoardColumns",
    columnIdField = "freeBoardColumnId",
    sortIndexField = "freeBoardSortIndex",
    persistPlacementOnTask = true,
    onPersistTaskPlacements,
}) {
    const translate = useTranslate();
    const permission = usePermission();
    const columnMenuRef = useRef(null);
    const [selectedTask, setSelectedTask] = useState();
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [columnConfigs, setColumnConfigs] = useState(() => normalizeFreeBoardColumns(boardColumns ?? projectData?.[columnStorageField], translate));
    const [columns, setColumns] = useState([]);
    const [collapsedColumnIds, setCollapsedColumnIds] = useState([]);
    const [columnMenuModel, setColumnMenuModel] = useState([]);
    const [columnDialogVisible, setColumnDialogVisible] = useState(false);
    const [columnDraft, setColumnDraft] = useState(null);
    const [taskDialogVisible, setTaskDialogVisible] = useState(false);
    const [taskDraft, setTaskDraft] = useState({title: "", columnId: ""});
    const [savingColumns, setSavingColumns] = useState(false);
    const [savingTask, setSavingTask] = useState(false);
    const [columnError, setColumnError] = useState("");
    const [taskError, setTaskError] = useState("");
    const [filters, setFilters] = useState(createTaskListFilters);
    // optimistic placements (taskId → column, order, status) shown until the saves finish, so cards never jump back
    const [pendingPlacements, setPendingPlacements] = useState({});
    const canManageColumns = projectAccess?.canEditProject === true;
    const canMoveTasks = projectAccess?.canChangeTaskStatus === true;
    const columnForTask = (task, configs = columnConfigs) => freeBoardColumnForTask(task, configs, columnIdField);
    const withPending = (task) => {
        const pending = pendingPlacements[taskKey(task)];
        if (!pending) return task;
        return {
            ...task,
            [columnIdField]: pending.columnId,
            [sortIndexField]: pending.sortIndex,
            ...(pending.taskStatus ? {taskStatus: pending.taskStatus, doneAt: pending.doneAt ?? null} : {}),
        };
    };
    const holdPlacements = (entries) => setPendingPlacements((current) => ({...current, ...entries}));
    const releasePlacements = (ids) => setPendingPlacements((current) => {
        const next = {...current};
        ids.forEach((id) => { delete next[id]; });
        return next;
    });
    const persistTaskPlacements = async (placements) => {
        if (typeof onPersistTaskPlacements === "function" && placements.length) {
            await onPersistTaskPlacements(placements.map(({task, column, index}) => ({
                taskId: taskKey(task),
                columnId: String(column.id),
                sortIndex: index,
            })));
        }
    };

    useEffect(() => {
        if (projectData) {
            permission.setScopeId(projectData.documentId);
            permission.setScopeRolePermissions(projectData?.rolesConfig);
        }
        // Permission methods have unstable identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectData]);

    useEffect(() => {
        if (resetFiltersKey) setFilters(createTaskListFilters());
    }, [resetFiltersKey]);

    useEffect(() => {
        setColumnConfigs(normalizeFreeBoardColumns(boardColumns ?? projectData?.[columnStorageField], translate));
        // Translation is stable for the active language.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boardColumns, columnStorageField, projectData?.documentId, projectData?.freeBoardColumns, projectData?.taskBoardColumns]);

    const persistColumns = async (nextColumns) => {
        if (!updateProject || !projectData) return false;
        const serialized = serializeFreeBoardColumns(nextColumns);
        const previous = columnConfigs;
        setColumnConfigs(serialized);
        setSavingColumns(true);
        setColumnError("");
        const updated = await updateProject(projectData, {[columnStorageField]: serialized});
        setSavingColumns(false);
        if (!updated) {
            setColumnConfigs(previous);
            setColumnError(translate("free-board.error.save"));
            return false;
        }
        return true;
    };

    const openCreateColumn = (insertIndex = columnConfigs.length, taskStatus = "todo") => {
        setColumnError("");
        setColumnDraft({id: `free-board-${Date.now().toString(36)}`, title: "", taskStatus, showSubtasks: true, insertIndex, isNew: true});
        setColumnDialogVisible(true);
    };

    const openEditColumn = (columnId) => {
        const config = columnConfigs.find((column) => String(column.id) === String(columnId));
        if (!config) return;
        setColumnError("");
        setColumnDraft({...config, isNew: false});
        setColumnDialogVisible(true);
    };

    const saveColumn = async () => {
        const title = String(columnDraft?.title || "").trim();
        if (!title) {
            setColumnError(translate("free-board.error.column-name"));
            return;
        }
        let next;
        const previousColumn = columnConfigs.find((column) => String(column.id) === String(columnDraft.id));
        if (columnDraft.isNew) {
            next = [...columnConfigs];
            next.splice(Math.max(0, Math.min(columnDraft.insertIndex, next.length)), 0, {...columnDraft, title});
        } else {
            next = columnConfigs.map((column) => String(column.id) === String(columnDraft.id) ? {...column, title, taskStatus: columnDraft.taskStatus} : column);
        }
        if (await persistColumns(next)) {
            if (previousColumn && previousColumn.taskStatus !== columnDraft.taskStatus) {
                const affected = (tasks || []).filter((task) => columnForTask(task, columnConfigs)?.id === previousColumn.id);
                await Promise.all(affected.map((task) => updateTask(task, {
                    taskStatus: columnDraft.taskStatus,
                    doneAt: columnDraft.taskStatus === "done" ? (task.doneAt || Date.now()) : null,
                })));
            }
            setColumnDialogVisible(false);
        }
    };

    const deleteColumnById = (columnId) => {
        if (columnConfigs.length <= 1) return;
        const deleting = columnConfigs.find((column) => String(column.id) === String(columnId));
        if (!deleting) return;
        confirmDialog({
            group: "free-board-column-delete",
            className: "rtl",
            header: translate("free-board.delete-column"),
            message: translate("free-board.delete-column-confirm"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: translate("delete"),
            rejectLabel: translate("cancel"),
            acceptClassName: "text-xs p-button-danger gap-2",
            rejectClassName: "text-xs p-button-text gap-2",
            defaultFocus: "reject",
            accept: async () => {
                const next = columnConfigs.filter((column) => String(column.id) !== String(deleting.id));
                const fallback = next[0];
                const affected = (tasks || []).filter((task) => columnForTask(task, columnConfigs)?.id === deleting.id);
                const placements = affected.map((task, index) => ({task, column: fallback, index}));
                await Promise.all(placements.map(({task, column, index}) => updateTask(task, {
                    ...(persistPlacementOnTask ? {[columnIdField]: column.id, [sortIndexField]: index} : {}),
                    taskStatus: column.taskStatus,
                    doneAt: column.taskStatus === "done" ? (task.doneAt || Date.now()) : null,
                })));
                await persistTaskPlacements(placements);
                if (await persistColumns(next)) setCollapsedColumnIds([]);
            },
        });
    };

    const setSubtaskVisibility = (columnId, showSubtasks) => persistColumns(columnConfigs.map((column) => (
        String(column.id) === String(columnId) ? {...column, showSubtasks} : column
    )));
    const collapseAllColumns = () => setCollapsedColumnIds(columnConfigs.map((column) => String(column.id)));
    const expandAllColumns = () => setCollapsedColumnIds([]);

    const openColumnMenu = (event, config) => {
        event.stopPropagation();
        const menuTarget = event.currentTarget;
        const index = columnConfigs.findIndex((column) => String(column.id) === String(config.id));
        setColumnMenuModel([
            {label: translate("free-board.rename-column"), icon: "pi pi-pencil", command: () => openEditColumn(config.id)},
            {label: translate("free-board.subtasks-visibility"), icon: "pi pi-sitemap", items: [
                {label: translate("free-board.hide-subtasks"), icon: "pi pi-eye-slash", disabled: config.showSubtasks === false, command: () => setSubtaskVisibility(config.id, false)},
                {label: translate("free-board.show-subtasks"), icon: "pi pi-eye", disabled: config.showSubtasks !== false, command: () => setSubtaskVisibility(config.id, true)},
            ]},
            {label: translate("free-board.add-column-nearby"), icon: "pi pi-plus", items: [
                {label: translate("free-board.add-column-right"), icon: "pi pi-arrow-right", command: () => openCreateColumn(index, config.taskStatus)},
                {label: translate("free-board.add-column-left"), icon: "pi pi-arrow-left", command: () => openCreateColumn(index + 1, config.taskStatus)},
            ]},
            {label: translate("free-board.columns-visibility"), icon: "pi pi-window-minimize", items: [
                {label: translate("free-board.collapse-all"), icon: "pi pi-angle-double-down", command: collapseAllColumns},
                {label: translate("free-board.expand-all"), icon: "pi pi-angle-double-up", command: expandAllColumns},
            ]},
            {separator: true},
            {label: translate("free-board.delete-column"), icon: "pi pi-trash", className: "text-red-500", disabled: columnConfigs.length <= 1, command: () => deleteColumnById(config.id)},
        ]);
        window.setTimeout(() => columnMenuRef.current?.toggle({currentTarget: menuTarget}), 0);
    };

    const openCreateTask = (columnId = columnConfigs[0]?.id) => {
        setTaskError("");
        setTaskDraft({title: "", columnId: String(columnId || columnConfigs[0]?.id || "")});
        setTaskDialogVisible(true);
    };

    const createTaskInColumn = async (title, columnId) => {
        const column = columnConfigs.find((item) => String(item.id) === String(columnId));
        if (!String(title || "").trim() || !column) return false;
        const columnTasks = (tasks || []).filter((task) => freeBoardColumnForTask(task, columnConfigs, columnIdField)?.id === column.id);
        const created = await createTask({
            title: String(title).trim(),
            taskStatus: column.taskStatus,
            doneAt: column.taskStatus === "done" ? Date.now() : null,
            ...(persistPlacementOnTask ? {[columnIdField]: column.id, [sortIndexField]: columnTasks.length} : {}),
        });
        if (created) await persistTaskPlacements([{task: created, column, index: columnTasks.length}]);
        return created;
    };

    const saveTask = async () => {
        if (!String(taskDraft.title || "").trim()) {
            setTaskError(translate("free-board.error.task-title"));
            return;
        }
        setSavingTask(true);
        const created = await createTaskInColumn(taskDraft.title, taskDraft.columnId);
        setSavingTask(false);
        if (!created) {
            setTaskError(translate("free-board.error.create-task"));
            return;
        }
        setFilters(createTaskListFilters());
        setTaskDialogVisible(false);
    };

    const completeQuickTask = () => {
        setFilters(createTaskListFilters());
    };

    const itemTemplate = (data) => {
        const task = data?.payload;
        const children = Array.isArray(task?.childrenTasks) ? task.childrenTasks : [];
        const typeLabel = entityLabel(task?.task_type);
        const priority = PRIORITY_OPTIONS.find((option) => option.id === task?.priority);
        const commentsCount = Array.isArray(task?.task_comments) ? task.task_comments.length : 0;
        const attachmentsCount = Array.isArray(task?.attachments) ? task.attachments.length : 0;
        const description = richTextToPlain(task?.description).trim();
        const hasDoneAt = Boolean(task?.doneAt);
        const startDate = formatDate(task?.startAt);
        const dueDate = formatDate(task?.dueDate);
        const assignee = relationMember(task?.assignee);
        const overdue = task?.dueDate && task?.taskStatus !== "done" && isPast(new Date(task.dueDate).getTime() / 1000, 1, "minute");
        const done = task?.taskStatus === "done";
        const doneInputId = `free-board-done-${taskKey(task)}`;
        return (
            <div data-task-sidebar-trigger="true" className={`p-3 border-round-xl border-1 cursor-pointer ${done ? "surface-100 border-200 opacity-70" : "surface-card border-200 shadow-1"}`} onClick={() => setSelectedTask(data)}>
                {(typeLabel || priority) && (
                    <div className="flex flex-wrap align-items-center gap-2 mb-2">
                        {typeLabel && <span className="inline-block text-3xs px-2 py-1 border-round bg-orange-50 text-orange-700 border-1 border-orange-100">{typeLabel}</span>}
                        {priority && <span className="inline-flex align-items-center gap-1 text-3xs px-2 py-1 border-round surface-50 border-1 border-200 text-700" title={translate("task.priority")}><span className="border-circle" style={{width: ".45rem", height: ".45rem", background: priority.color}}/>{translate("task.priority")}: {priority.label}</span>}
                    </div>
                )}
                <div className="flex align-items-start justify-content-between gap-2">
                    <span className="inline-flex flex-shrink-0 pt-1" title={translate("free-board.card.mark-done")} onClick={(event) => event.stopPropagation()} onPointerDown={(event) => event.stopPropagation()}>
                        <Checkbox inputId={doneInputId} checked={done} disabled={!canMoveTasks} onChange={(event) => toggleTaskDone(task, event.checked)} aria-label={translate("free-board.card.mark-done")}/>
                    </span>
                    <p className={`m-0 text-xs font-medium line-height-3 flex-1 min-w-0 ${task?.occurrenceStatus === "skipped" || done ? "line-through text-500" : ""}`}>{data?.label} <RecurrenceBadge task={task}/></p>
                    <span className="inline-flex align-items-center flex-shrink-0" onClick={(event) => event.stopPropagation()}>
                        <TaskFocusControls task={task} compact/>
                    </span>
                </div>
                <TaskDecisionTag task={task} className="mt-2"/>
                {description && <p className="m-0 mt-2 text-2xs text-600 line-height-3 white-space-pre-line" aria-label={translate("task-description")}>{description}</p>}
                {/*
                {progress && <ProgressBar value={progress.percent} showValue={false} className="h-1rem mt-3" pt={{value: {className: "bg-primary"}}}/>} 
                */}
                {assignee && <div className="flex align-items-center gap-2 mt-2"><span className="text-3xs text-500">{translate("assignee")}:</span><Avatar className="text-3xs" style={{width: "1.5rem", height: "1.5rem"}} memberData={assignee}/></div>}
                <TaskCardChecklist task={task} disabled={!projectAccess?.canUpdateTask} updateTask={updateTask}/>
                {(startDate || dueDate) && (
                    <div className="flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top-1 border-100">
                        {startDate && <span className="inline-flex align-items-center gap-1 text-3xs text-500"><Icon icon="mi mi:calendar_today" className="text-xs"/>{translate("free-board.start-prefix")} {startDate}</span>}
                        {dueDate && <span className={`inline-flex align-items-center gap-1 text-3xs font-medium ${overdue ? "text-red-500" : "text-primary"}`}><Icon icon="mi mi:event_upcoming" className="text-xs"/>{translate("free-board.end-prefix")} {dueDate}</span>}
                    </div>
                )}
                {(attachmentsCount > 0 || children.length > 0 || hasDoneAt || commentsCount > 0) && (
                    <div className="flex flex-wrap align-items-center gap-3 mt-2 pt-2 border-top-1 border-100 text-3xs text-500">
                        {attachmentsCount > 0 && <span className="inline-flex align-items-center" title={translate("free-board.card.has-attachments")} aria-label={translate("free-board.card.has-attachments")}><Icon icon="mi mi:attach_file" className="text-xs"/></span>}
                        {children.length > 0 && <span className="inline-flex align-items-center" title={translate("free-board.card.has-subtasks")} aria-label={translate("free-board.card.has-subtasks")}><Icon icon="mi mi:account_tree" className="text-xs"/></span>}
                        {hasDoneAt && <span className="inline-flex align-items-center gap-1 text-green-500" title={translate("free-board.card.completed")}><Icon icon="mi mi:event_available" className="text-xs"/>{formatDate(task.doneAt)}</span>}
                        {commentsCount > 0 && <span className="inline-flex align-items-center gap-1" title={translate("free-board.comments")}><Icon icon="mi mi:chat_bubble_outline" className="text-xs"/>{commentsCount}</span>}
                    </div>
                )}
            </div>
        );
    };

    const renderQuickAdd = (config) => (
        <FreeBoardQuickTaskComposer
            key={config.id}
            columnId={String(config.id)}
            onCreate={createTaskInColumn}
            onCreated={completeQuickTask}
        />
    );

    const buildColumns = (configs, boardTasks) => configs.map((config) => {
        const collapsed = collapsedColumnIds.includes(String(config.id));
        const columnTasks = (boardTasks || []).map(withPending).filter((task) => columnForTask(task, configs)?.id === config.id);
        const items = collapsed ? [] : columnTasks
            .sort((left, right) => (
                // finished work goes to the bottom of its column, the rest keeps the user's order
                Number(left?.taskStatus === "done") - Number(right?.taskStatus === "done")
                || (left?.[sortIndexField] ?? Number.MAX_SAFE_INTEGER) - (right?.[sortIndexField] ?? Number.MAX_SAFE_INTEGER)
            ))
            .map((task) => ({id: taskKey(task), label: task.title, draggable: canMoveTasks, payload: task, showSubtasks: config.showSubtasks !== false, template: itemTemplate}));
        return {
            id: config.id,
            payload: config.taskStatus,
            collapsed,
            label: collapsed ? (
                <button type="button" className="p-0 border-none bg-transparent cursor-pointer flex align-items-center justify-content-center h-full w-full text-color" style={{writingMode: "vertical-rl", transform: "rotate(180deg)"}} aria-label={translate("free-board.expand-all")} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => {event.stopPropagation(); expandAllColumns();}}>
                    <strong className="text-xs white-space-nowrap">{config.title}</strong>
                </button>
            ) : (
                <div className="flex align-items-center justify-content-between w-full gap-2">
                    <strong className="text-xs">{config.title}</strong>
                    <span className="flex align-items-center gap-1">
                        {projectAccess?.canCreateTask && <Button type="button" text rounded icon="pi pi-plus" className="p-0 w-2rem h-2rem" aria-label={translate("free-board.add-task")} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => {event.stopPropagation(); window.dispatchEvent(new CustomEvent("free-board:open-quick-task", {detail: {columnId: String(config.id)}}));}}/>}
                        {canManageColumns && <Button type="button" text rounded icon="pi pi-ellipsis-h" className="p-0 w-2rem h-2rem" aria-label={translate("free-board.column-actions")} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => openColumnMenu(event, config)}/>} 
                    </span>
                </div>
            ),
            draggable: canManageColumns,
            items,
            footer: !collapsed && projectAccess?.canCreateTask ? renderQuickAdd(config) : null,
        };
    });

    useEffect(() => {
        setColumns(buildColumns(columnConfigs, filteredTasks));
        // Templates follow permission state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnConfigs, filteredTasks, collapsedColumnIds, canManageColumns, canMoveTasks, projectAccess?.canCreateTask, pendingPlacements]);
    useUpdateEffect(() => setColumns(buildColumns(columnConfigs, filteredTasks)), [tasks]);

    const onBoardChange = async (nextColumns, info) => {
        setColumns(nextColumns);
        if (info?.active?.type === "column") {
            const byId = new Map(columnConfigs.map((column) => [String(column.id), column]));
            await persistColumns(nextColumns.map((column) => byId.get(String(column.id))).filter(Boolean));
            return;
        }
        if (info?.active?.type !== "item" || !canMoveTasks) return;
        const affectedIds = new Set([String(info.from.columnId), String(info.to.columnId)]);
        const configById = new Map(columnConfigs.map((column) => [String(column.id), column]));
        const updates = nextColumns.filter((column) => affectedIds.has(String(column.id))).flatMap((column) => column.items.map((item, index) => {
            const task = withPending(item.payload);
            const target = configById.get(String(column.id));
            const source = columnForTask(task);
            // a card that changed column takes that column's status; the others only get a new order
            const changedColumn = String(source?.id) !== String(target?.id);
            // …except a card ticked «تکمیل کار»: it stays done while it moves between open columns; only dragging it
            // out of a «done» column reopens it
            const keepsDone = task?.taskStatus === "done" && source?.taskStatus !== "done" && target?.taskStatus !== "done";
            const changedStatus = changedColumn && !keepsDone && task?.taskStatus !== target?.taskStatus;
            return {
                task,
                column: target,
                index,
                changedColumn,
                changedStatus,
                changedOrder: changedColumn || task?.[sortIndexField] !== index,
                taskStatus: changedStatus ? target.taskStatus : null,
                doneAt: changedStatus ? (target.taskStatus === "done" ? (task.doneAt || Date.now()) : null) : null,
            };
        })).filter((update) => update.column && update.changedOrder);
        if (!updates.length) return;
        const ids = updates.map(({task}) => taskKey(task));
        holdPlacements(Object.fromEntries(updates.map((update) => [taskKey(update.task), {
            columnId: String(update.column.id),
            sortIndex: update.index,
            ...(update.changedStatus ? {taskStatus: update.taskStatus, doneAt: update.doneAt} : {}),
        }])));
        try {
            await persistTaskPlacements(updates);
            await Promise.all(updates
                .filter((update) => update.changedStatus || persistPlacementOnTask)
                .map((update) => updateTask(update.task, {
                    ...(persistPlacementOnTask ? {[columnIdField]: update.column.id, [sortIndexField]: update.index} : {}),
                    ...(update.changedStatus ? {taskStatus: update.taskStatus, doneAt: update.doneAt} : {}),
                })));
        } finally {
            releasePlacements(ids);
        }
    };

    // the card checkbox: done work stays on its board and moves to the bottom; un-done work in a "done" column moves
    // back to the first open column
    const toggleTaskDone = async (rawTask, checked) => {
        if (!canMoveTasks) return;
        const task = withPending(rawTask);
        const current = columnForTask(task);
        if (!current) return;
        const reopenTo = !checked && current.taskStatus === "done"
            ? (columnConfigs.find((column) => column.taskStatus !== "done") || current)
            : current;
        const columnTasks = (tasks || []).map(withPending).filter((item) => columnForTask(item)?.id === reopenTo.id && taskKey(item) !== taskKey(task));
        const index = checked ? columnTasks.length : 0;
        const taskStatus = checked ? "done" : (reopenTo.taskStatus === "done" ? "todo" : reopenTo.taskStatus);
        const doneAt = checked ? Date.now() : null;
        const id = taskKey(task);
        holdPlacements({[id]: {columnId: String(reopenTo.id), sortIndex: index, taskStatus, doneAt}});
        try {
            await updateTask(task, {
                taskStatus,
                doneAt,
                ...(persistPlacementOnTask ? {[columnIdField]: reopenTo.id, [sortIndexField]: index} : {}),
            });
            await persistTaskPlacements([{task, column: reopenTo, index}]);
        } finally {
            releasePlacements([id]);
        }
    };

    const allColumnsCollapsed = columnConfigs.length > 0 && collapsedColumnIds.length === columnConfigs.length;
    const columnDialogFooter = <div className="flex align-items-center justify-content-end gap-2 w-full"><Button type="button" text severity="secondary" label={translate("cancel")} className="text-xs" onClick={() => setColumnDialogVisible(false)}/><Button type="button" icon="pi pi-check" label={translate("free-board.save-column")} className="text-xs gap-2" loading={savingColumns} onClick={saveColumn}/></div>;
    const taskDialogFooter = <div className="flex align-items-center justify-content-end gap-2 w-full"><Button type="button" text severity="secondary" label={translate("cancel")} className="text-xs" onClick={() => setTaskDialogVisible(false)}/><Button type="button" icon="pi pi-check" label={translate("free-board.add-task")} className="text-xs gap-2" loading={savingTask} onClick={saveTask}/></div>;
    const selectedTaskEntity = (tasks || []).find((task) => taskKey(task) === String(selectedTask?.id || ""));

    return (
        <div className="h-full flex flex-column min-h-0 overflow-hidden">
            <div className="zofino-task-list-filters border-bottom-1 border-200 flex align-items-start gap-2 flex-shrink-0 surface-card">
                <div className="flex-1 min-w-0">
                    <TaskListFilter
                        showDateFilter
                        tasks={tasks}
                        filters={filters}
                        onFiltersChange={setFilters}
                        onChange={setFilteredTasks}
                    />
                </div>
                {projectAccess?.canCreateTask && <Button type="button" icon="pi pi-plus" label={translate("free-board.add-task")} className="text-xs gap-2 ml-3 mt-3 flex-shrink-0" onClick={() => openCreateTask()}/>} 
            </div>
            {columnError && <Message severity="error" text={columnError} className="m-2 text-xs flex-shrink-0"/>}
            <div data-testid="free-board-scroll-container" className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden mb-2 surface-card">
                <DndSortable
                    draggable={canMoveTasks || canManageColumns}
                    draggableColumns={canManageColumns}
                    draggableItems={canMoveTasks}
                    columns={columns}
                    boardClassName={`h-full align-items-start ${allColumnsCollapsed ? "flex-row-reverse" : ""}`}
                    boardStyle={{width: "max-content", minWidth: "100%", padding: "1.5rem", paddingBottom: "0.5rem", gap: "1.5rem"}}
                    columnWrapperClassName="h-full flex-none"
                    columnWrapperStyle={(column) => ({flex: column.collapsed ? "0 0 3rem" : "0 0 18.75rem", width: column.collapsed ? "3rem" : "18.75rem", minWidth: column.collapsed ? "3rem" : "18.75rem"})}
                    columnClassName={(column) => `p-3 h-full overflow-hidden border-round-xl surface-50 border-1 border-200 ${column.collapsed ? "w-3rem cursor-pointer" : "w-19rem"}`}
                    columnHeaderClassName={(column) => column.collapsed ? "h-full w-full justify-content-center cursor-pointer" : "w-full cursor-move"}
                    showColumnDragHandle={false}
                    dragColumnFromHeader
                    columnBodyClassName={(column) => column.collapsed ? "hidden" : "flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide pr-1"}
                    columnItemsClassName="pb-2"
                    appendContent={canManageColumns ? <div className="w-19rem flex-none"><Button type="button" text icon="pi pi-plus" label={translate("free-board.add-board")} className="text-xs gap-2 w-full justify-content-start border-1 border-dashed border-300 text-500" onClick={() => openCreateColumn()}/></div> : null}
                    onChange={onBoardChange}
                />
            </div>
            <TieredMenu ref={columnMenuRef} model={columnMenuModel} popup dir="rtl" submenuIcon={<Icon icon="mi mi:chevron_left" className="text-lg"/>} className="rtl text-xs w-15rem" pt={{action: {className: "flex align-items-center gap-2 white-space-nowrap"}, icon: {className: "m-0 flex-shrink-0"}, label: {className: "white-space-nowrap"}, submenu: {className: "rtl w-15rem", style: {right: "100%", left: "auto"}}, submenuIcon: {className: "mr-auto ml-0"}}}/>
            <Dialog visible={columnDialogVisible} modal dir="rtl" header={translate(columnDraft?.isNew ? "free-board.add-column" : "free-board.edit-column")} className="w-11 sm:w-30rem rtl" footer={columnDialogFooter} onHide={() => setColumnDialogVisible(false)}>
                <div className="p-3 flex flex-column gap-3">{columnError && <Message severity="error" text={columnError} className="text-xs"/>}<div className="flex flex-column gap-2"><label htmlFor="free-board-column-title" className="font-bold text-xs">{translate("free-board.column-name")}</label><InputText id="free-board-column-title" className="w-full text-xs" value={columnDraft?.title || ""} onChange={(event) => setColumnDraft((current) => ({...current, title: event.target.value}))}/></div><div className="flex flex-column gap-2"><label htmlFor="free-board-column-status" className="font-bold text-xs">{translate("free-board.column-status")}</label><Dropdown inputId="free-board-column-status" className="w-full text-xs" panelClassName="rtl text-xs" value={columnDraft?.taskStatus || "todo"} options={FREE_BOARD_STATUS_OPTIONS.map((status) => ({value: status, label: translate(status)}))} onChange={(event) => setColumnDraft((current) => ({...current, taskStatus: event.value}))}/><small className="text-2xs text-500">{translate("free-board.column-status-help")}</small></div></div>
            </Dialog>
            <Dialog visible={taskDialogVisible} modal dir="rtl" header={translate("free-board.add-task")} className="w-11 sm:w-30rem rtl" footer={taskDialogFooter} onHide={() => setTaskDialogVisible(false)}>
                <div className="p-3 flex flex-column gap-3">{taskError && <Message severity="error" text={taskError} className="text-xs"/>}<div className="flex flex-column gap-2"><label htmlFor="free-board-task-title" className="font-bold text-xs">{translate("free-board.task-title")}</label><InputText id="free-board-task-title" className="w-full text-xs" value={taskDraft.title} onChange={(event) => setTaskDraft((current) => ({...current, title: event.target.value}))}/></div><div className="flex flex-column gap-2"><label htmlFor="free-board-task-column" className="font-bold text-xs">{translate("free-board.target-column")}</label><Dropdown inputId="free-board-task-column" className="w-full text-xs" panelClassName="rtl text-xs" value={taskDraft.columnId} options={columnConfigs.map((column) => ({value: String(column.id), label: column.title}))} onChange={(event) => setTaskDraft((current) => ({...current, columnId: event.value}))}/></div></div>
            </Dialog>
            <ConfirmDialog group="free-board-column-delete" className="rtl"/>
            {tasks && <TaskSidebar disabled={!projectAccess?.canUpdateTask} boardColumns={sidebarBoardColumns === undefined ? columnConfigs : sidebarBoardColumns} selectedTask={selectedTaskEntity} setSelectedTask={setSelectedTask} updateTask={updateTask} onOtherTaskClicked={(task) => setSelectedTask(tasks.find((item) => taskKey(item) === taskKey(task)))}/>} 
        </div>
    );
}

export default FreeBoard;
